import { useMemo, useState, useCallback, useRef, useEffect } from 'react';

/**
 * @template T, E
 * @typedef {{
 *  data: T,
 *  isLoading: boolean,
 *  error: E | null,
 *  methods: {
 *      onInit: () => void,
 *      onError: (error: E) => void,
 *      onLoad: (value: T) => void
 *  }
 * }} RequestState
 */

/**
 * @template T, E
 *
 * Simpler alternative to having to create three different state hooks
 * when managing API requests. This only uses one state, which, depending
 * on how you have your useEffects set up, could reduce unnecessary renders
 *
 * @returns {RequestState<T, E>}
 */
export default function UseRequestState() {
    const [state, setState] = useState(makeState());
    const isMountedRef = useRef(true);
    const resetState = useCallback((updates) => {
        if (!isMountedRef.current) return;
        setState(makeState(updates));
    }, []);

    // If a component is unmounted before the request it made is complete,
    // make sure we don't perform any actions to state
    useEffect(() => () => (isMountedRef.current = false), []);

    const { data, isLoading, error } = state;

    // Note: Make sure methods is memoized. The vast majority of hooks that
    // use useRequestState() have hooks themselves that are triggered by
    // "methods".
    const methods = useMemo(
        () => ({
            onInit: () => setState((currentState) => ({ ...currentState, isLoading: true, error: null })),
            onError: (err) => resetState({ error: err }),
            onLoad: (value) => setState({ data: value, isLoading: false, error: null }),
        }),
        [resetState]
    );

    return { data, isLoading, error, methods };
}

function makeState(overrides) {
    return {
        data: null,
        isLoading: false,
        error: null,

        ...(overrides || {}),
    };
}
